import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

// Import refactored components
import {
  DeleteConfirmDialog,
  UserTable,
  ReviewTable,
  AlertReportsTab,
  RedFlagsTab,
  DiscreteAlertsTab,
  SoundSettingsTab,
  CallMessagesTab
} from '../components/admin';

const Admin = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', is_admin: false });
  const [soundSettings, setSoundSettings] = useState(null);
  const [uploadingSound, setUploadingSound] = useState(null);
  const [callSettings, setCallSettings] = useState(null);
  const [uploadingCall, setUploadingCall] = useState(null);
  const [updatingCall, setUpdatingCall] = useState(null);
  const [needsAttentionLabel, setNeedsAttentionLabel] = useState('Urgent Alert');
  const [customMessageLabel, setCustomMessageLabel] = useState('Thank You Call');
  const [customMessageText, setCustomMessageText] = useState('');
  const [button3Label, setButton3Label] = useState('Alert 3');
  const [button4Label, setButton4Label] = useState('Alert 4');
  const [showExportHistory, setShowExportHistory] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [alertExportHistory, setAlertExportHistory] = useState([]);
  const [showAlertExportHistory, setShowAlertExportHistory] = useState(false);
  
  // Anonymous devices state
  const [deviceStats, setDeviceStats] = useState(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  
  // Spreadsheet management state - Users
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSortBy, setUserSortBy] = useState('created_at');
  const [userSortOrder, setUserSortOrder] = useState('desc');
  const [userFilterBy, setUserFilterBy] = useState('all');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Spreadsheet management state - Reviews
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [reviewSortBy, setReviewSortBy] = useState('created_at');
  const [reviewSortOrder, setReviewSortOrder] = useState('desc');
  const [reviewFilterBy, setReviewFilterBy] = useState('all');
  const [reviewSearchTerm, setReviewSearchTerm] = useState('');

  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', id: '', name: '' });

  // Password reset dialog state
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ show: false, user: null });
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  // Red flag management state
  const [redFlaggedUsers, setRedFlaggedUsers] = useState([]);
  const [blockedAttempts, setBlockedAttempts] = useState([]);
  const [loadingRedFlags, setLoadingRedFlags] = useState(false);

  // Discrete alerts state
  const [discreteAlerts, setDiscreteAlerts] = useState([]);
  const [loadingDiscreteAlerts, setLoadingDiscreteAlerts] = useState(false);
  const [discreteAlertsSearch, setDiscreteAlertsSearch] = useState('');
  const [selectedDiscreteAlerts, setSelectedDiscreteAlerts] = useState([]);
  const [selectedAlertLogs, setSelectedAlertLogs] = useState([]);
  const [selectedRedFlagUsers, setSelectedRedFlagUsers] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, reviewsRes, soundsRes, callsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/reviews`),
        axios.get(`${API}/sounds`),
        axios.get(`${API}/calls/settings`)
      ]);
      setUsers(usersRes.data);
      setReviews(reviewsRes.data);
      setSoundSettings(soundsRes.data);
      setCallSettings(callsRes.data);
      setNeedsAttentionLabel(callsRes.data?.needs_attention_label || 'Urgent Alert');
      setCustomMessageLabel(callsRes.data?.custom_message_label || 'Thank You Call');
      setCustomMessageText(callsRes.data?.custom_message_text || '');
      setButton3Label(callsRes.data?.button3_label || 'Alert 3');
      setButton4Label(callsRes.data?.button4_label || 'Alert 4');
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch red-flagged users
  const fetchRedFlaggedUsers = async () => {
    setLoadingRedFlags(true);
    try {
      const token = localStorage.getItem('token');
      const [flaggedRes, attemptsRes] = await Promise.all([
        axios.get(`${API}/admin/red-flagged-users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/blocked-alert-attempts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setRedFlaggedUsers(flaggedRes.data);
      setBlockedAttempts(attemptsRes.data);
    } catch (error) {
      toast.error('Failed to fetch red flag data');
    } finally {
      setLoadingRedFlags(false);
    }
  };

  // Enable user alerts
  const handleEnableUserAlerts = async (userId, userName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/enable-alerts`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Alerts re-enabled for ${userName}`);
      fetchRedFlaggedUsers();
      fetchData();
      if (discreteAlerts.length > 0) fetchDiscreteAlerts();
    } catch (error) {
      toast.error('Failed to enable alerts');
    }
  };

  // Disable user alerts
  const handleDisableUserAlerts = async (userId, userName) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users/${userId}/disable-alerts`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Alerts disabled for ${userName}`);
      fetchRedFlaggedUsers();
      fetchData();
      if (discreteAlerts.length > 0) fetchDiscreteAlerts();
    } catch (error) {
      toast.error('Failed to disable alerts');
    }
  };

  // Fetch anonymous device statistics
  const fetchDeviceStats = async () => {
    setLoadingDevices(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/devices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeviceStats(response.data);
    } catch (error) {
      console.error('Failed to fetch device stats:', error);
      toast.error('Failed to load device statistics');
    } finally {
      setLoadingDevices(false);
    }
  };

  // Export devices CSV
  const handleExportDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-devices`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `loo_report_devices_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Devices exported successfully!');
    } catch (error) {
      toast.error('Failed to export devices');
    }
  };

  // Fetch discrete alerts with full details
  const fetchDiscreteAlerts = async () => {
    setLoadingDiscreteAlerts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/discrete-alerts-full`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDiscreteAlerts(response.data);
    } catch (error) {
      toast.error('Failed to fetch discrete alerts');
    } finally {
      setLoadingDiscreteAlerts(false);
    }
  };

  // Toggle user alert status from discrete alerts table
  const toggleUserAlertStatus = async (userId, userName, currentlyDisabled) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = currentlyDisabled 
        ? `${API}/admin/users/${userId}/enable-alerts`
        : `${API}/admin/users/${userId}/disable-alerts`;
      
      await axios.post(endpoint, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(currentlyDisabled 
        ? `Alerts re-enabled for ${userName}` 
        : `Alerts disabled for ${userName}`
      );
      fetchDiscreteAlerts();
      fetchData();
    } catch (error) {
      toast.error('Failed to update alert status');
    }
  };

  // Bulk delete discrete alerts
  const handleBulkDeleteDiscreteAlerts = async () => {
    if (selectedDiscreteAlerts.length === 0) {
      toast.error('No alerts selected');
      return;
    }
    setDeleteConfirm({ 
      show: true, 
      type: 'bulk-discrete-alerts', 
      id: selectedDiscreteAlerts.join(','), 
      name: `${selectedDiscreteAlerts.length} discrete alert(s)` 
    });
  };

  // Bulk delete alert logs
  const handleBulkDeleteAlertLogs = async () => {
    if (selectedAlertLogs.length === 0) {
      toast.error('No alert logs selected');
      return;
    }
    setDeleteConfirm({ 
      show: true, 
      type: 'bulk-alert-logs', 
      id: selectedAlertLogs.join(','), 
      name: `${selectedAlertLogs.length} alert log(s)` 
    });
  };

  // Bulk clear red flags (re-enable alerts for selected users)
  const handleBulkClearRedFlags = async () => {
    if (selectedRedFlagUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    setDeleteConfirm({ 
      show: true, 
      type: 'bulk-clear-redflags', 
      id: selectedRedFlagUsers.join(','), 
      name: `${selectedRedFlagUsers.length} user(s)` 
    });
  };

  // Execute bulk operations
  const executeBulkOperation = async (type, ids) => {
    const idList = ids.split(',');
    const token = localStorage.getItem('token');
    
    try {
      if (type === 'bulk-discrete-alerts') {
        for (const id of idList) {
          await axios.delete(`${API}/admin/discrete-alerts/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        toast.success(`${idList.length} discrete alert(s) deleted`);
        setSelectedDiscreteAlerts([]);
        fetchDiscreteAlerts();
      } else if (type === 'bulk-alert-logs') {
        for (const id of idList) {
          await axios.delete(`${API}/admin/alert-logs/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        toast.success(`${idList.length} alert log(s) deleted`);
        setSelectedAlertLogs([]);
        fetchAlertLogs();
      } else if (type === 'bulk-clear-redflags') {
        for (const userId of idList) {
          await axios.post(`${API}/admin/users/${userId}/enable-alerts`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        toast.success(`Red flags cleared for ${idList.length} user(s)`);
        setSelectedRedFlagUsers([]);
        fetchRedFlaggedUsers();
        fetchData();
      }
    } catch (error) {
      toast.error(`Failed to complete bulk operation`);
    }
  };

  const handleSoundUpload = async (buttonId, file) => {
    if (!file) return;
    
    const validExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    const hasAudioType = file.type && file.type.startsWith('audio/');
    
    if (!hasAudioType && !hasValidExtension) {
      toast.error('Please upload an audio file (mp3, wav, ogg, m4a, aac, flac, or wma)');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingSound(buttonId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/admin/sounds/upload/${buttonId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${buttonId.charAt(0).toUpperCase() + buttonId.slice(1)} sound uploaded successfully!`);
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload sound');
    } finally {
      setUploadingSound(null);
    }
  };

  const handleUploadNeedsAttentionAudio = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Only .mp3 files are allowed for call messages');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingCall('needs_attention');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/calls/upload-needs-attention?label=${encodeURIComponent(needsAttentionLabel)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Button 1 audio uploaded successfully!');
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploadingCall(null);
    }
  };

  const handleUploadButton3Audio = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Only .mp3 files are allowed for call messages');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingCall('button3');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/calls/upload-button3?label=${encodeURIComponent(button3Label)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Button 3 audio uploaded successfully!');
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploadingCall(null);
    }
  };

  const handleUploadButton4Audio = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Only .mp3 files are allowed for call messages');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploadingCall('button4');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/calls/upload-button4?label=${encodeURIComponent(button4Label)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Button 4 audio uploaded successfully!');
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploadingCall(null);
    }
  };

  const handleUpdateCallMessage = async (messageType) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    const messageText = customMessageText.trim();
    if (!messageText || messageText.length < 10) {
      toast.error('Message must be at least 10 characters long');
      return;
    }
    if (messageText.length > 1000) {
      toast.error('Message must be less than 1000 characters');
      return;
    }

    setUpdatingCall(messageType);
    try {
      await axios.post(`${API}/calls/update-custom`, {
        message_text: messageText,
        label: customMessageLabel
      }, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });

      toast.success('Custom message updated successfully!');
      setTimeout(() => fetchData(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update call message');
    } finally {
      setUpdatingCall(null);
    }
  };

  const playSound = (soundUrl) => {
    if (!soundUrl) {
      toast.error('No sound file available');
      return;
    }
    try {
      const fullUrl = soundUrl.startsWith('/api') 
        ? `${API.replace('/api', '')}${soundUrl}` 
        : `${API}${soundUrl}`;
      const cacheBustedUrl = `${fullUrl}?t=${Date.now()}`;
      const audio = new Audio(cacheBustedUrl);
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('Audio playback failed:', err.message);
          toast.error('Failed to play sound - try tapping the button again');
        });
      }
    } catch (err) {
      console.warn('Audio initialization failed:', err.message);
      toast.error('Audio not supported on this device');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      is_admin: user.is_admin || false
    });
  };

  const handleSaveUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}?name=${editForm.name}&email=${editForm.email}&is_admin=${editForm.is_admin}`);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    setDeleteConfirm({ show: true, type: 'user', id: userId, name: userName });
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/reset-password`, {
        user_id: resetPasswordDialog.user.id,
        new_password: newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Password reset for ${resetPasswordDialog.user.email}`);
      setResetPasswordDialog({ show: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteReview = async (reviewId, restaurantName) => {
    setDeleteConfirm({ show: true, type: 'review', id: reviewId, name: restaurantName });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ show: false, type: '', id: '', name: '' });
    
    // Handle bulk deletes for users/reviews
    if (type === 'bulk-users' || type === 'bulk-reviews') {
      await executeBulkDelete(type, id);
      return;
    }
    
    // Handle bulk operations for alerts/logs/redflags
    if (type === 'bulk-discrete-alerts' || type === 'bulk-alert-logs' || type === 'bulk-clear-redflags') {
      await executeBulkOperation(type, id);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (type === 'user') {
        await axios.delete(`${API}/admin/users/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('User deleted successfully');
      } else if (type === 'review') {
        await axios.delete(`${API}/admin/reviews/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Review deleted successfully');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, type: '', id: '', name: '' });
  };

  const fetchExportHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExportHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch export history');
    }
  };

  const handleDownloadExport = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/download-export/${filename}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Failed to download export');
    }
  };

  const handleShowExportHistory = async () => {
    setShowExportHistory(true);
    await fetchExportHistory();
  };

  // Alert Reports Functions
  const fetchAlertLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/alert-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAlertLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch alert logs');
    }
  };

  const handleExportAlertLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-alert-logs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download with proper handling for all devices including iPad/Safari
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const filename = `alert_logs_${new Date().toISOString().split('T')[0]}.csv`;
      
      // Use FileSaver-style approach for better cross-browser support
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // For Safari/iOS - need to use setTimeout
        setTimeout(() => {
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        }, 0);
      }
      
      toast.success('Alert logs exported - check your Downloads folder');
      fetchAlertExportHistory();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export alert logs');
    }
  };

  const fetchAlertExportHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/alert-export-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAlertExportHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch alert export history');
    }
  };

  const handleShowAlertExportHistory = async () => {
    setShowAlertExportHistory(true);
    await fetchAlertExportHistory();
  };

  // User Table Helper Functions
  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    const usersToDelete = selectedUsers.filter(id => id !== user.id);
    if (usersToDelete.length === 0) {
      toast.error('Cannot delete your own account');
      return;
    }
    setDeleteConfirm({ show: true, type: 'bulk-users', id: usersToDelete.join(','), name: `${usersToDelete.length} user(s)` });
  };

  const handleBulkDeleteReviews = async () => {
    if (selectedReviews.length === 0) {
      toast.error('No reviews selected');
      return;
    }
    setDeleteConfirm({ show: true, type: 'bulk-reviews', id: selectedReviews.join(','), name: `${selectedReviews.length} review(s)` });
  };

  const executeBulkDelete = async (type, ids) => {
    const idList = ids.split(',');
    try {
      const token = localStorage.getItem('token');
      for (const id of idList) {
        const endpoint = type === 'bulk-users' ? `${API}/admin/users/${id}` : `${API}/admin/reviews/${id}`;
        await axios.delete(endpoint, { headers: { 'Authorization': `Bearer ${token}` } });
      }
      toast.success(`${idList.length} ${type === 'bulk-users' ? 'user(s)' : 'review(s)'} deleted successfully`);
      if (type === 'bulk-users') {
        setSelectedUsers([]);
      } else {
        setSelectedReviews([]);
      }
      fetchData();
    } catch (error) {
      toast.error(`Failed to delete some ${type === 'bulk-users' ? 'users' : 'reviews'}`);
    }
  };

  // Export/Print functions for Users - Server-side download for reliability
  const handleExportUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download with proper handling for all devices including iPad/Safari
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const filename = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      // Use FileSaver-style approach for better cross-browser support
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // For Safari/iOS - need to use setTimeout
        setTimeout(() => {
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        }, 0);
      }
      
      toast.success('Users exported to CSV - check your Downloads folder');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export users');
    }
  };

  const handlePrintUsers = () => {
    const printContent = `
      <html><head><title>Users List - The Loo Report</title>
        <style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#dc2626}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #000;padding:8px;text-align:left}th{background:#f3f4f6}.admin{color:#dc2626;font-weight:bold}</style>
      </head><body>
        <h1>Users List - The Loo Report</h1>
        <p>Total: ${users.length} users | Exported: ${new Date().toLocaleString()}</p>
        <table><thead><tr><th>Name</th><th>Email</th><th>Admin</th><th>Created</th></tr></thead>
        <tbody>${users.map(u => `<tr><td>${u.name}</td><td>${u.email}</td><td class="${u.is_admin ? 'admin' : ''}">${u.is_admin ? 'Yes' : 'No'}</td><td>${new Date(u.created_at).toLocaleDateString()}</td></tr>`).join('')}</tbody></table>
      </body></html>`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Export/Print functions for Reviews - Server-side download for reliability
  const handleExportReviewsCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/export-reviews`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download with proper handling for all devices including iPad/Safari
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const filename = `reviews_export_${new Date().toISOString().split('T')[0]}.csv`;
      
      // Use FileSaver-style approach for better cross-browser support
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        // IE/Edge
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // For Safari/iOS - need to use setTimeout
        setTimeout(() => {
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 100);
        }, 0);
      }
      
      toast.success('Reviews exported to CSV - check your Downloads folder');
      fetchExportHistory(); // Refresh export history
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export reviews');
    }
  };

  const handlePrintReviews = () => {
    const ratingLabels = ['', 'MAJOR ISSUES', 'NEEDS ATTENTION', "IT'S OK", 'EXCEPTIONAL'];
    const printContent = `
      <html><head><title>Reviews List - The Loo Report</title>
        <style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#dc2626}table{width:100%;border-collapse:collapse;margin-top:20px;font-size:11px}th,td{border:1px solid #000;padding:6px;text-align:left}th{background:#f3f4f6}</style>
      </head><body>
        <h1>Reviews List - The Loo Report</h1>
        <p>Total: ${reviews.length} reviews | Exported: ${new Date().toLocaleString()}</p>
        <table><thead><tr><th>Restaurant</th><th>City</th><th>Rating</th><th>Clean</th><th>Supplies</th><th>Maint</th><th>Reviewer</th><th>Date</th></tr></thead>
        <tbody>${reviews.map(r => `<tr><td>${r.restaurant_name}</td><td>${r.city}</td><td>${ratingLabels[r.overall_rating]}</td><td>${r.general_condition}/10</td><td>${r.supplies}/10</td><td>${r.maintenance}/10</td><td>${r.reviewer_name}</td><td>${new Date(r.created_at).toLocaleDateString()}</td></tr>`).join('')}</tbody></table>
      </body></html>`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-zinc-900">
        <p className="text-yellow-400 text-2xl font-bold">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen toilet-humor-bg text-gray-900">
      {/* Full Width Header Image */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-50 border-b-4 border-black overflow-hidden">
        <div className="relative">
          <img 
            src="https://customer-assets.emergentagent.com/job_loo-tracker/artifacts/2igfkpva_Background%20for%20APP%20Home%20Screen%20no%20head.jpg" 
            alt="The Loo Report™ - Know Before You Go!"
            className="w-full h-auto object-contain"
            style={{ maxHeight: '126px', display: 'block' }}
          />
          <span className="absolute bottom-1 right-2 text-[14px] font-bold text-black" style={{ fontFamily: 'Arial, sans-serif' }}>™</span>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b-4 border-black py-4 px-4 sm:px-6 shadow-lg pt-40">
        <div className="max-w-7xl mx-auto flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl text-red-600"
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '4px', textShadow: 'none', fontWeight: '400' }}>
            ADMIN PANEL
          </h1>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/')} variant="outline" className="border-2 border-black bg-yellow-400 hover:bg-yellow-500">
              🏠 Home
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-2 border-black">
              📋 View Reviews
            </Button>
            <Button 
              onClick={() => { localStorage.removeItem('token'); window.location.href = '/'; }} 
              variant="outline" className="border-2 border-red-600 text-red-600"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs - Dropdown Menu */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-4 mb-6">
          <label className="text-lg font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Select Section:
          </label>
          <select
            value={activeTab}
            onChange={(e) => {
              const tab = e.target.value;
              setActiveTab(tab);
              if (tab === 'alerts') fetchAlertLogs();
              if (tab === 'redflags') fetchRedFlaggedUsers();
              if (tab === 'discrete') fetchDiscreteAlerts();
              if (tab === 'devices') fetchDeviceStats();
            }}
            className="px-6 py-3 text-lg bg-white border-4 border-black rounded-lg font-bold text-red-600 cursor-pointer hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
            style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.25rem', letterSpacing: '3px', minWidth: '280px' }}
            data-testid="admin-menu-dropdown"
          >
            <option value="users">👥 USERS ({users.length})</option>
            <option value="devices">📱 ANONYMOUS DEVICES</option>
            <option value="reviews">📝 REVIEWS ({reviews.length})</option>
            <option value="sounds">🔊 SOUND SETTINGS</option>
            <option value="calls">📞 CALL MESSAGES</option>
            <option value="alerts">📊 ALERT REPORTS</option>
            <option value="redflags">🚩 RED FLAGS</option>
            <option value="discrete">📞 URGENT ALERTS</option>
            <option value="legacy">🗄️ LEGACY REVIEWS DESIGN</option>
          </select>
        </div>

        {/* Legacy Dashboard Link */}
        {activeTab === 'legacy' && (
          <div className="bg-white border-4 border-black rounded-lg p-8 text-center">
            <h2 className="text-3xl text-red-600 font-bold mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}>
              Legacy Reviews Design
            </h2>
            <p className="text-gray-700 mb-6 font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              This is the original detailed review card design with sliders and expanded information.
            </p>
            <a 
              href="/dashboard-legacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-8 border-4 border-black rounded-lg text-xl"
              style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '2px' }}
            >
              Open Legacy Dashboard →
            </a>
          </div>
        )}

        {/* Users Tab - Using Component */}
        {activeTab === 'users' && (
          <UserTable
            users={users}
            currentUserId={user.id}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            userSortBy={userSortBy}
            setUserSortBy={setUserSortBy}
            userSortOrder={userSortOrder}
            setUserSortOrder={setUserSortOrder}
            userFilterBy={userFilterBy}
            setUserFilterBy={setUserFilterBy}
            userSearchTerm={userSearchTerm}
            setUserSearchTerm={setUserSearchTerm}
            onEditUser={handleEditUser}
            onDeleteUser={handleDeleteUser}
            onBulkDelete={handleBulkDeleteUsers}
            onExport={handleExportUsers}
            onPrint={handlePrintUsers}
            onResetPassword={(u) => setResetPasswordDialog({ show: true, user: u })}
          />
        )}

        {/* Reviews Tab - Using Component */}
        {activeTab === 'reviews' && (
          <ReviewTable
            reviews={reviews}
            selectedReviews={selectedReviews}
            setSelectedReviews={setSelectedReviews}
            reviewSortBy={reviewSortBy}
            setReviewSortBy={setReviewSortBy}
            reviewSortOrder={reviewSortOrder}
            setReviewSortOrder={setReviewSortOrder}
            reviewFilterBy={reviewFilterBy}
            setReviewFilterBy={setReviewFilterBy}
            reviewSearchTerm={reviewSearchTerm}
            setReviewSearchTerm={setReviewSearchTerm}
            onDeleteReview={handleDeleteReview}
            onBulkDelete={handleBulkDeleteReviews}
            onExport={handleExportReviewsCSV}
            onPrint={handlePrintReviews}
            onShowHistory={handleShowExportHistory}
            onRefresh={fetchData}
          />
        )}

        {/* Anonymous Devices Tab */}
        {activeTab === 'devices' && (
          <div className="bg-white border-4 border-black rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl text-red-600 font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}>
                📱 ANONYMOUS DEVICE TRACKING
              </h2>
              <Button
                onClick={handleExportDevices}
                className="bg-green-500 hover:bg-green-600 text-white font-bold px-4 py-2 border-2 border-black rounded"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                📥 Export CSV
              </Button>
            </div>
            
            {loadingDevices ? (
              <div className="text-center py-8">
                <p className="text-gray-600 font-bold">Loading device data...</p>
              </div>
            ) : deviceStats ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black text-blue-600">{deviceStats.total_devices}</p>
                    <p className="text-sm font-bold text-blue-800">Total Devices</p>
                  </div>
                  <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black text-green-600">{deviceStats.total_reviews}</p>
                    <p className="text-sm font-bold text-green-800">Total Reviews</p>
                  </div>
                  <div className="bg-orange-100 border-2 border-orange-300 rounded-lg p-4 text-center">
                    <p className="text-3xl font-black text-orange-600">{deviceStats.total_alerts}</p>
                    <p className="text-sm font-bold text-orange-800">Total Alerts</p>
                  </div>
                </div>
                
                {/* Devices Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-black">
                        <th className="px-4 py-3 text-left font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Device ID</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Reviews</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Alerts</th>
                        <th className="px-4 py-3 text-center font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Total</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Linked Email</th>
                        <th className="px-4 py-3 text-left font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deviceStats.devices.map((device, index) => (
                        <tr key={device.device_id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="px-4 py-3 font-mono text-xs text-gray-600">
                            {device.device_id.substring(0, 16)}...
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-green-100 text-green-700 font-bold px-2 py-1 rounded">{device.review_count}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-orange-100 text-orange-700 font-bold px-2 py-1 rounded">{device.alert_count}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-blue-100 text-blue-700 font-bold px-2 py-1 rounded">{device.total_contributions}</span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {device.linked_email ? (
                              <span className="text-green-600 font-bold">{device.linked_email}</span>
                            ) : (
                              <span className="text-gray-400 italic">Anonymous</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {device.created_at ? new Date(device.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {deviceStats.devices.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 font-bold">No anonymous devices found</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Button
                  onClick={fetchDeviceStats}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-3 border-2 border-black rounded"
                >
                  Load Device Data
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Sound Settings Tab */}
        {activeTab === 'sounds' && soundSettings && (
          <SoundSettingsTab
            soundSettings={soundSettings}
            uploadingSound={uploadingSound}
            onSoundUpload={handleSoundUpload}
            onPlaySound={playSound}
          />
        )}

        {/* Call Messages Tab - Using Component */}
        {activeTab === 'calls' && callSettings && (
          <CallMessagesTab
            callSettings={callSettings}
            uploadingCall={uploadingCall}
            updatingCall={updatingCall}
            needsAttentionLabel={needsAttentionLabel}
            setNeedsAttentionLabel={setNeedsAttentionLabel}
            customMessageLabel={customMessageLabel}
            setCustomMessageLabel={setCustomMessageLabel}
            customMessageText={customMessageText}
            setCustomMessageText={setCustomMessageText}
            button3Label={button3Label}
            setButton3Label={setButton3Label}
            button4Label={button4Label}
            setButton4Label={setButton4Label}
            onUploadNeedsAttentionAudio={handleUploadNeedsAttentionAudio}
            onUploadButton3Audio={handleUploadButton3Audio}
            onUploadButton4Audio={handleUploadButton4Audio}
            onUpdateCallMessage={handleUpdateCallMessage}
            onPlaySound={playSound}
          />
        )}

        {/* Alert Reports Tab - Using Component */}
        {activeTab === 'alerts' && (
          <AlertReportsTab
            alertLogs={alertLogs}
            selectedAlertLogs={selectedAlertLogs}
            setSelectedAlertLogs={setSelectedAlertLogs}
            onBulkDelete={handleBulkDeleteAlertLogs}
            onExport={handleExportAlertLogs}
            onShowHistory={handleShowAlertExportHistory}
            onRefresh={fetchAlertLogs}
          />
        )}

        {/* Red Flags Tab - Using Component */}
        {activeTab === 'redflags' && (
          <RedFlagsTab
            redFlaggedUsers={redFlaggedUsers}
            blockedAttempts={blockedAttempts}
            selectedRedFlagUsers={selectedRedFlagUsers}
            setSelectedRedFlagUsers={setSelectedRedFlagUsers}
            loadingRedFlags={loadingRedFlags}
            onEnableAlerts={handleEnableUserAlerts}
            onDisableAlerts={handleDisableUserAlerts}
            onBulkClearRedFlags={handleBulkClearRedFlags}
          />
        )}

        {/* Discrete Alerts Tab - Using Component */}
        {activeTab === 'discrete' && (
          <DiscreteAlertsTab
            discreteAlerts={discreteAlerts}
            selectedDiscreteAlerts={selectedDiscreteAlerts}
            setSelectedDiscreteAlerts={setSelectedDiscreteAlerts}
            discreteAlertsSearch={discreteAlertsSearch}
            setDiscreteAlertsSearch={setDiscreteAlertsSearch}
            loadingDiscreteAlerts={loadingDiscreteAlerts}
            onToggleUserAlertStatus={toggleUserAlertStatus}
            onBulkDelete={handleBulkDeleteDiscreteAlerts}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog - Using Component */}
      <DeleteConfirmDialog
        deleteConfirm={deleteConfirm}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.show} onOpenChange={() => { setResetPasswordDialog({ show: false, user: null }); setNewPassword(''); }}>
        <DialogContent className="bg-white border-8 border-black max-w-md">
          <DialogHeader>
            <DialogTitle className="text-3xl text-red-600" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {resetPasswordDialog.user && (
              <>
                <div className="bg-gray-100 p-3 rounded border-2 border-black">
                  <p className="font-bold">{resetPasswordDialog.user.name}</p>
                  <p className="text-gray-600 text-sm">{resetPasswordDialog.user.email}</p>
                </div>
                <div>
                  <Label htmlFor="new-password" className="text-red-600">New Password</Label>
                  <Input 
                    id="new-password" 
                    type="text"
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="mt-2 bg-white border-2 border-black" 
                    data-testid="new-password-input" 
                  />
                </div>
                <div className="flex gap-3 justify-end mt-6">
                  <Button onClick={() => { setResetPasswordDialog({ show: false, user: null }); setNewPassword(''); }} variant="outline" className="border-2 border-black">Cancel</Button>
                  <Button 
                    onClick={handleResetPassword} 
                    disabled={resettingPassword || newPassword.length < 6}
                    className="bg-yellow-500 text-black hover:bg-yellow-600 font-bold" 
                    data-testid="confirm-reset-password-btn"
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editingUser !== null} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-white border-8 border-black max-w-md">
          <DialogHeader>
            <DialogTitle className="text-3xl text-red-600" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="edit-name" className="text-red-600">Name</Label>
              <Input id="edit-name" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                className="mt-2 bg-white border-2 border-black" data-testid="edit-name-input" />
            </div>
            <div>
              <Label htmlFor="edit-email" className="text-red-600">Email</Label>
              <Input id="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                className="mt-2 bg-white border-2 border-black" data-testid="edit-email-input" />
            </div>
            <div className="flex items-center space-x-3">
              <Checkbox id="edit-admin" checked={editForm.is_admin} onCheckedChange={(checked) => setEditForm({...editForm, is_admin: checked})}
                data-testid="edit-admin-checkbox" />
              <Label htmlFor="edit-admin" className="text-gray-700 cursor-pointer">Admin Access</Label>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <Button onClick={() => setEditingUser(null)} variant="outline" className="border-2 border-black">Cancel</Button>
              <Button onClick={handleSaveUser} className="bg-red-600 text-white hover:bg-red-700" data-testid="save-user-btn">Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export History Dialog */}
      <Dialog open={showExportHistory} onOpenChange={setShowExportHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-red-600 font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}>
              Export History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {exportHistory.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No exports found</p>
            ) : (
              exportHistory.map((exp) => (
                <div key={exp.id} className="bg-white p-4 rounded border-2 border-black">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-bold text-lg">{exp.filename}</p>
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <p>📅 Created: {new Date(exp.created_at).toLocaleString()}</p>
                        <p>👤 By: {exp.created_by}</p>
                        <p>📊 Reviews: {exp.review_count}</p>
                      </div>
                    </div>
                    <Button onClick={() => handleDownloadExport(exp.filename)} className="bg-green-600 hover:bg-green-700 text-white border-2 border-black">
                      ⬇️ Download
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Alert Export History Dialog */}
      <Dialog open={showAlertExportHistory} onOpenChange={setShowAlertExportHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-red-600 font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '3px' }}>
              Alert Export History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {alertExportHistory.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No alert exports found</p>
            ) : (
              alertExportHistory.map((exp) => (
                <div key={exp.id} className="bg-white p-4 rounded border-2 border-black">
                  <p className="font-bold text-lg">{exp.filename}</p>
                  <div className="text-sm text-gray-600 mt-1 space-y-1">
                    <p>📅 Created: {new Date(exp.created_at).toLocaleString()}</p>
                    <p>👤 By: {exp.created_by}</p>
                    <p>📊 Alerts: {exp.alert_count}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
